import { Inter } from '@/assets/fonts/font';
import { formatDate } from '@/lib/format-date';
import i18next from 'i18next';
import jsPDF from 'jspdf';

const t = i18next.t;

/**
 * Helper: underline field row
 */
const fieldRow = (label: string, unit?: string) => `
  <div style="display: flex; align-items: flex-end; height: 22px;">
    <span style="font-weight: 500; white-space: nowrap; font-size: 12px;">${label}</span>
    <div style="flex-grow: 1; border-bottom: 1px solid #4a4a4a; margin: 0 8px;"></div>
    ${unit ? `<span style="font-size: 12px;">${unit}</span>` : ''}
  </div>
`;

/**
 * Helper: signature row
 */
const signatureRow = (label: string) => `
  <div style="display: flex; align-items: flex-end; height: 22px;">
    <span style="font-weight: 500; white-space: nowrap; font-size: 12px;">${label}</span>
    <div style="flex-grow: 1; border-bottom: 1px solid #4a4a4a; margin-left: 8px;"></div>
  </div>
`;

/**
 * Single document block generator (~330px height)
 */
const getDocBlock = (
  contract: any,
  org: any,
  id: string,
  created_at: string,
  farmer_tax_number: string,
) => {
  return `
    <div style="display: flex; flex-direction: column; padding: 10px 14px; gap: 6px;">

      <!-- Top Section -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start;">
        <div style="display: flex; flex-direction: column; gap: 2px;">
          <p style="font-size: 12px; margin: 0;">${t('grain.receiving.enterprise')}: ${contract?.buyer_name || '"Sangzor" MCHJ'}</p>
          <p style="font-size: 12px; margin: 0;">${t('grain.storage.place')}: ${org?.place_name || 'Korxona qoshi (Andijon)'}</p>
          <p style="font-size: 12px; margin: 0;">${t('delivering.farmer.name')}: ${contract?.farmer_name || 'OYBEK F/X'}</p>
        </div>
        <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 2px;">
          <p style="font-size: 15px; font-weight: 700; margin: 0;">DQIM-3</p>
          <p style="font-size: 12px; margin: 0;">
            ${t('delivering.farmer.stir')}:
            <span style="font-weight: 600;">${farmer_tax_number || '321234567'}</span>
          </p>
          <p style="font-size: 12px; margin: 0;">
            ${t('id.number.colon')}
            <span style="font-weight: 700;">${id || '1234567'}</span>
          </p>
        </div>
      </div>

      <!-- Date -->
      <div style="text-align: right;">
        <p style="font-size: 12px; font-weight: 500; margin: 0;">${formatDate(created_at) || '19.11.2025'}</p>
      </div>

      <!-- Title Section -->
      <div style="text-align: center; margin: 4px 0;">
        <p style="font-weight: 500; font-size: 12px; margin: 0;">${t('grain.analysis.results')}</p>
        <p style="font-size: 16px; font-weight: 700; margin: 2px 0 0 0;">${t('information.note')}</p>
      </div>

      <!-- Laboratoriya hulosasi -->
      <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 2px;">
        <p style="font-size: 13px; font-weight: 600; margin: 0;">${t('laboratory.conclusion')}</p>
        <p style="font-size: 12px; font-weight: 500; margin: 0;">${org?.district_name || "Ulug'nor tumani"}</p>
      </div>

      <!-- 2-column form fields -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; column-gap: 28px; row-gap: 0; margin-top: 2px;">
        ${fieldRow(t('grain.crop.type'))}
        ${fieldRow(t('grain.pollution'), '%')}
        ${fieldRow(t('grain.humidity'), '%')}
        ${fieldRow(t('grain.naturasi'), 'gr')}
        ${fieldRow(t('grain.mixture'), '%')}
        ${fieldRow(t('grain.vitreous'), '%')}
        ${fieldRow(t('grain.gluten'), '%')}
        ${fieldRow(t('grain.class'))}
      </div>

      <!-- Signatures -->
      <div style="display: grid; grid-template-columns: 1fr 1fr; column-gap: 28px; row-gap: 0; margin-top: 6px;">
        ${signatureRow(t('farm.representative'))}
        <div></div>
        ${signatureRow(t('senior.laboratory.assistant'))}
        ${signatureRow(t('laboratory.manager'))}
      </div>

    </div>
  `;
};

/**
 * PDF generator
 */
export const generateGrainPDF = (
  contract: any,
  org: any,
  id: string,
  created_at: string,
  farmer_tax_number: string,
) => {
  return new Promise((resolve, reject) => {
    try {
      const repeatCount = 3;

      const blocks = Array.from({ length: repeatCount })
        .map(() =>
          getDocBlock(contract, org, id, created_at, farmer_tax_number),
        )
        .join(
          '<div style="margin: 12px 14px; border-bottom: 1px dashed #aaa;"></div>',
        );

      const template = `
        <!DOCTYPE html>
        <html lang="ru">
        <head>
          <meta charset="UTF-8">
          <title>PDF Content</title>
          <style>
            * {
              font-family: 'Inter', sans-serif;
              box-sizing: border-box;
            }
            body { margin: 0; padding: 0; }
            p { margin: 0; padding: 0; }
          </style>
        </head>
        <body>
          <div class="container" style="color: #121212;">
            ${blocks}
          </div>
        </body>
        </html>
      `;

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [794, 1123],
      });

      pdf.addFileToVFS('Inter.ttf', Inter);
      pdf.addFont('Inter.ttf', 'Inter', 'normal');
      pdf.setFont('Inter', 'normal');

      pdf.html(template, {
        callback: function (doc) {
          const pdfData = doc.output('datauristring');
          const base64 = pdfData.split('base64,')[1];
          resolve(base64);
        },
        x: 10,
        y: 10,
        width: 780,
        windowWidth: 900,
      });
    } catch (error) {
      reject(error);
    }
  });
};
